import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GlassCard } from '@/components/ui/glass-card'
import { ArrowRight } from 'lucide-react'
import { submitContactForm, RECAPTCHA_SITE_KEY } from '@/lib/contact-api'
import { CaptchaWidget } from '@gjl/site-kit/captcha'

type Question = {
  id: string
  prompt: string
  options: { value: string; label: string }[]
}

const questions: Question[] = [
  {
    id: 'capital',
    prompt: 'How much are you considering committing per deal?',
    options: [
      { value: 'under-25k', label: 'Under $25K' },
      { value: '25k-50k', label: '$25K – $50K' },
      { value: '50k-100k', label: '$50K – $100K' },
      { value: 'over-100k', label: 'Over $100K' },
    ],
  },
  {
    id: 'horizon',
    prompt: 'How long can your capital stay invested without needing access?',
    options: [
      { value: 'under-2y', label: 'Less than 2 years' },
      { value: '2-5y', label: '2 – 5 years' },
      { value: '5-10y', label: '5 – 10 years' },
      { value: '10y-plus', label: '10+ years' },
    ],
  },
  {
    id: 'liquidity',
    prompt: 'How important is the ability to exit quickly?',
    options: [
      { value: 'high', label: 'Very important — I may need access within months' },
      { value: 'medium', label: 'Somewhat — I could wait a year if needed' },
      { value: 'low', label: 'Not important — I plan in decades' },
    ],
  },
  {
    id: 'returns',
    prompt: 'Which return profile best fits your goals?',
    options: [
      { value: 'cashflow-first', label: '8 – 12% cash-on-cash from day one + slow appreciation' },
      { value: 'value-add', label: '20%+ IRR through aggressive value-add and exit' },
      { value: 'learning', label: "I'm still learning what's realistic" },
    ],
  },
  {
    id: 'involvement',
    prompt: 'How involved do you want to be in operations?',
    options: [
      { value: 'fully-passive', label: 'Fully passive — quarterly reports and distributions' },
      { value: 'engaged', label: 'Engaged — I want quarterly calls and the ability to ask questions' },
      { value: 'active', label: 'Active — I want a say in operating decisions' },
    ],
  },
  {
    id: 'accreditation',
    prompt: 'Are you an accredited investor?',
    options: [
      { value: 'accredited', label: 'Yes' },
      { value: 'non-accredited', label: 'No' },
      { value: 'unsure', label: 'Not sure' },
    ],
  },
  {
    id: 'experience',
    prompt: 'How familiar are you with passive real estate investing?',
    options: [
      { value: 'first-time', label: 'First-time investor' },
      { value: 'some', label: "I've invested in 1 – 2 deals or REITs" },
      { value: 'experienced', label: "I've invested in 3+ deals across operators" },
    ],
  },
  {
    id: 'priority',
    prompt: 'What matters most when you pick an operator?',
    options: [
      { value: 'track-record', label: 'Closed-deal track record' },
      { value: 'reporting', label: 'Reporting transparency' },
      { value: 'alignment', label: 'Founder alignment / skin in the game' },
      { value: 'returns', label: 'Highest projected returns' },
    ],
  },
]

type Answers = Record<string, string>

const FAIRMEADOW_FIT: Answers = {
  capital: '25k-50k',
  horizon: '5-10y',
  liquidity: 'low',
  returns: 'cashflow-first',
  involvement: 'fully-passive',
  experience: 'some',
  priority: 'reporting',
}

function scoreFit(answers: Answers): {
  level: 'strong' | 'partial' | 'weak'
  matchedCount: number
  totalScored: number
  flags: string[]
} {
  const scoredKeys = Object.keys(FAIRMEADOW_FIT)
  let matchedCount = 0
  for (const key of scoredKeys) {
    if (answers[key] === FAIRMEADOW_FIT[key]) {
      matchedCount += 1
    } else if (key === 'capital' && answers[key] === '50k-100k') {
      matchedCount += 1
    } else if (key === 'horizon' && answers[key] === '10y-plus') {
      matchedCount += 1
    } else if (key === 'liquidity' && answers[key] === 'medium') {
      matchedCount += 0.5
    } else if (key === 'experience' && answers[key] === 'experienced') {
      matchedCount += 1
    } else if (key === 'priority' && (answers[key] === 'track-record' || answers[key] === 'alignment')) {
      matchedCount += 0.75
    }
  }

  const flags: string[] = []
  if (answers.capital === 'under-25k') flags.push('Below the $25K-per-deal minimum we typically structure around.')
  if (answers.capital === 'over-100k')
    flags.push('Above our typical $100K cap — we would structure that conversation differently.')
  if (answers.horizon === 'under-2y') flags.push('Under-2-year horizons do not fit a 5 – 10 year hold strategy.')
  if (answers.liquidity === 'high')
    flags.push('Real estate is illiquid; a months-level liquidity need is a structural mismatch.')
  if (answers.returns === 'value-add') flags.push('We underwrite for cash flow first, not 20%+ IRR exits.')
  if (answers.involvement === 'active')
    flags.push('We run a fully passive LP model; active operating involvement is not part of our structure.')

  const ratio = matchedCount / scoredKeys.length
  let level: 'strong' | 'partial' | 'weak' = 'weak'
  if (ratio >= 0.7 && flags.length === 0) level = 'strong'
  else if (ratio >= 0.45 && flags.length <= 1) level = 'partial'
  return { level, matchedCount, totalScored: scoredKeys.length, flags }
}

function buildMessage(answers: Answers, fit: ReturnType<typeof scoreFit>): string {
  const labelFor = (qid: string, value: string) => {
    const q = questions.find((x) => x.id === qid)
    if (!q) return value
    return q.options.find((o) => o.value === value)?.label ?? value
  }

  const lines = [
    'Submitted via fit assessment.',
    '',
    `Fit score: ${fit.level.toUpperCase()} (${fit.matchedCount}/${fit.totalScored} on scored axes)`,
    '',
    'Answers:',
    ...questions.map((q) => `  - ${q.prompt} → ${labelFor(q.id, answers[q.id] ?? '')}`),
  ]
  if (fit.flags.length) {
    lines.push('', 'Flags:')
    for (const f of fit.flags) lines.push(`  - ${f}`)
  }
  return lines.join('\n')
}

function deriveInvestorType(answers: Answers, level: string): string {
  return [
    `fit-${level}`,
    answers.capital ?? 'capital-unknown',
    answers.horizon ?? 'horizon-unknown',
    answers.accreditation ?? 'accred-unknown',
  ].join('|')
}

export default function FitAssessmentPage() {
  const [answers, setAnswers] = useState<Answers>({})
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [captchaToken, setCaptchaToken] = useState('')
  // The honeypot. Free, always on, never sends — parity with the contact form,
  // which had it and this page did not.
  const [hp, setHp] = useState('')

  const allAnswered = useMemo(() => questions.every((q) => answers[q.id]), [answers])
  const fit = useMemo(() => (allAnswered ? scoreFit(answers) : null), [answers, allAnswered])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!fit) return
    setStatus('loading')
    try {
      // Same absolute-origin + verified-envelope path as the contact form. This
      // page had the identical bug and was missed in the first pass: a relative
      // `/api/contact` that CloudFront answered with the SPA shell, so every
      // completed fit assessment — the investor QUALIFICATION form — was
      // discarded while the visitor got the full results screen. See
      // `lib/contact-api.ts`.
      await submitContactForm({
        name,
        email,
        message: buildMessage(answers, fit),
        investorType: deriveInvestorType(answers, fit.level),
        _hp: hp,
        captchaToken,
      })
      setStatus('success')
    } catch {
      setStatus('error')
      // A reCAPTCHA token is single-use. Leaving the stale one in state means
      // the retry fails with `captcha-failed` no matter what the visitor does.
      setCaptchaToken('')
    }
  }

  if (status === 'success' && fit) {
    return (
      <div className="bg-background text-foreground">
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-5">
            <GlassCard accent className="py-12 text-center">
              <h1 className="text-fluid-h2 text-foreground mb-4">Thanks — Garrett will be in touch.</h1>
              <p className="text-muted-foreground mx-auto mb-6 max-w-xl">
                You'll hear back within 1 – 2 business days. Here's the summary that was sent so you know what we saw.
              </p>
              <pre className="text-muted-foreground bg-card/50 overflow-x-auto rounded-lg p-4 text-left text-sm whitespace-pre-wrap">
                {buildMessage(answers, fit)}
              </pre>
              <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                <Link to="/track-record">
                  <Button>See the Track Record</Button>
                </Link>
                <Link to="/operator-questions">
                  <Button variant="outline">Read the Operator Questions</Button>
                </Link>
              </div>
            </GlassCard>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="border-border border-b">
        <div className="mx-auto max-w-5xl px-5 pt-14 pb-12 md:px-8 md:pt-20 md:pb-16">
          <h1 className="text-fluid-h1 text-navy mb-6 max-w-4xl">Are we a fit for each other?</h1>
          <p className="text-fluid-body-lg text-muted-foreground max-w-2xl">
            Eight questions. Honest answers — including the ones that say no. Submit and you'll hear from Garrett within
            1 – 2 business days.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="reveal mx-auto max-w-3xl">
          <GlassCard>
            <div className="mb-8 flex items-center gap-3">
              <h2 className="text-foreground text-2xl font-semibold">Fit assessment</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {questions.map((q, idx) => (
                <fieldset key={q.id} className="space-y-3">
                  <legend className="text-foreground text-base font-medium">
                    <span className="text-primary mr-2">{idx + 1}.</span>
                    {q.prompt}
                  </legend>
                  <div className="space-y-2">
                    {q.options.map((opt) => {
                      const checked = answers[q.id] === opt.value
                      return (
                        <label
                          key={opt.value}
                          className={`block cursor-pointer rounded-lg border p-3 transition-colors ${
                            checked ? 'border-primary bg-primary/10' : 'border-border hover:bg-card/50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            value={opt.value}
                            checked={checked}
                            onChange={() => setAnswers({ ...answers, [q.id]: opt.value })}
                            required
                            className="sr-only"
                          />
                          <span className="text-foreground text-sm">{opt.label}</span>
                        </label>
                      )
                    })}
                  </div>
                </fieldset>
              ))}

              <div className="border-border space-y-5 border-t pt-8">
                <div>
                  <label htmlFor="fit-name" className="text-foreground mb-2 block text-sm font-medium">
                    Your name
                  </label>
                  <Input
                    id="fit-name"
                    name="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="fit-email" className="text-foreground mb-2 block text-sm font-medium">
                    Email
                  </label>
                  <Input
                    id="fit-email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              {/*
                The honeypot, identical to the contact form's — off-screen
                rather than `display:none` (some bots skip that), out of the tab
                order, hidden from the accessibility tree, and `autoComplete`
                off so a browser cannot fill it for a real person and discard
                their assessment. Ids are suffixed because both forms can be
                open in the same document during a client-side route change.
              */}
              <div className="absolute top-auto left-[-9999px] h-px w-px overflow-hidden" aria-hidden="true">
                <label htmlFor="_hp_fit">Leave this field empty</label>
                <input
                  id="_hp_fit"
                  name="_hp"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                />
              </div>

              {/*
                ⚠️ THIS FORM POSTED TO A CAPTCHA-PROTECTED ENDPOINT WITH NO
                WIDGET. `CONTACT_CAPTCHA_REQUIRED` is "true" on
                `fairmeadow-capital-prod-api`, `verifyCaptcha` fails closed on a
                missing token, and this page never rendered one — so every
                completed fit assessment came back 403 `captcha-missing` and the
                visitor got the error card. Verified live on 2026-09-04 by
                POSTing to https://api.fairmeadow.capital/contact without a
                token. The contact form's own comment predicted it in as many
                words: "a form with no widget and a server that wants one
                returns 403". The prediction was right and nobody checked the
                second form against it.
              */}
              <CaptchaWidget siteKey={RECAPTCHA_SITE_KEY || undefined} onToken={setCaptchaToken} theme="dark" />

              {status === 'error' && (
                <div className="text-destructive bg-destructive/10 rounded-lg p-3 text-sm">
                  Something went wrong. Please try again or email garrett@fairmeadow.capital directly.
                </div>
              )}

              <Button type="submit" size="lg" className="w-full" disabled={!allAnswered || status === 'loading'}>
                {status === 'loading' ? 'Submitting...' : 'Submit assessment'}
                {status !== 'loading' && <ArrowRight className="ml-2 h-5 w-5" />}
              </Button>

              {!allAnswered && (
                <p className="text-muted-foreground text-center text-sm">Answer all eight questions to submit.</p>
              )}
            </form>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
